import { useState } from "react";
import { STATES_DATA } from "../data/constants";
import { getLangFont, selectStyles } from "../utils/helpers";

/**
 * RegisterPage
 * Three-step flow: Form → Sending (animated WA preview) → Done (confirmation).
 *
 * Props:
 *   lang {string}
 *   t    {object} – translations
 */
export default function RegisterPage({ lang, t }) {
  const bf = getLangFont(lang);

  // ── State ──
  const [state,    setState]    = useState("");
  const [district, setDistrict] = useState("");
  const [mandi,    setMandi]    = useState("");
  const [name,     setName]     = useState("");
  const [mobile,   setMobile]   = useState("");
  const [errors,   setErrors]   = useState({});
  const [step,     setStep]     = useState("form");   // "form" | "sending" | "done"
  const [waStep,   setWaStep]   = useState(0);         // 0-3 animation stages

  const districts = state    ? Object.keys(STATES_DATA[state]?.districts || {}) : [];
  const mandis    = district  ? (STATES_DATA[state]?.districts[district] || [])  : [];

  // ── Validation ──
  const validate = () => {
    const e = {};
    if (!name.trim())              e.name     = true;
    if (!/^[6-9]\d{9}$/.test(mobile)) e.mobile   = true;
    if (!state)                    e.state    = true;
    if (!district)                 e.district = true;
    if (!mandi)                    e.mandi    = true;
    return e;
  };

  const submit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setStep("sending");
    setTimeout(() => setWaStep(1), 600);
    setTimeout(() => setWaStep(2), 1400);
    setTimeout(() => setWaStep(3), 2400);
    setTimeout(() => setStep("done"), 3000);
  };

  const inputStyle = (hasError) => ({
    width:        "100%",
    padding:      "12px 14px",
    borderRadius: 10,
    boxSizing:    "border-box",
    border:       `1.5px solid ${hasError ? "#ef4444" : "#d1c7b0"}`,
    fontFamily:   bf,
    fontSize:     14,
    outline:      "none",
    background:   "#fff",
    color:        "#333",
  });

  // ── Sending screen ────────────────────────────────────── //
  if (step === "sending") {
    return (
      <div style={{ maxWidth: 420, margin: "80px auto", padding: "0 16px", textAlign: "center", fontFamily: bf }}>
        <div
          style={{
            background:   "#fff",
            borderRadius: 24,
            padding:      "48px 32px",
            boxShadow:    "0 8px 40px rgba(0,0,0,0.1)",
            border:       "1px solid #e8e0d0",
          }}
        >
          <div style={{ fontSize: 56, marginBottom: 16 }}>📲</div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: "#1a4d2e", marginBottom: 8 }}>
            {lang === "en" ? "Sending WhatsApp..." : "WhatsApp भेजा जा रहा है..."}
          </h2>

          {/* Progress steps */}
          <div style={{ marginTop: 28, textAlign: "left" }}>
            {[
              { label: lang === "en" ? "✅ Registration saved"         : "✅ पंजीकरण सहेजा गया",               done: waStep >= 1 },
              { label: lang === "en" ? `📤 Sending confirmation to +91 ${mobile}` : `📤 +91 ${mobile} पर संदेश भेजा जा रहा है`, done: waStep >= 2 },
              { label: lang === "en" ? "🎉 WhatsApp delivered!"        : "🎉 WhatsApp पहुंच गया!",              done: waStep >= 3 },
            ].map((s, i) => (
              <div
                key={i}
                style={{
                  display:    "flex",
                  alignItems: "center",
                  gap:        12,
                  marginBottom: 16,
                  opacity:    s.done ? 1 : 0.3,
                  transition: "opacity 0.4s ease",
                }}
              >
                <div
                  style={{
                    width:          32,
                    height:         32,
                    borderRadius:   "50%",
                    background:     s.done ? "#25D366" : "#e8e0d0",
                    display:        "flex",
                    alignItems:     "center",
                    justifyContent: "center",
                    fontSize:       14,
                    flexShrink:     0,
                    transition:     "background 0.4s",
                  }}
                >
                  {s.done ? "✓" : i + 1}
                </div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: s.done ? "#1a1a1a" : "#9ca3af" }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* Message preview bubble */}
          <div style={{ marginTop: 24, background: "#075E54", borderRadius: 14, padding: "16px 18px", textAlign: "left" }}>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>
              {lang === "en" ? "Message being sent" : "भेजा जा रहा संदेश"}
            </div>
            <div
              style={{
                background:   "#DCF8C6",
                color:        "#333",
                borderRadius: "12px 12px 12px 3px",
                padding:      "12px 14px",
                fontFamily:   "monospace",
                fontSize:     12,
                lineHeight:   1.9,
              }}
            >
              🌾 नमस्ते {name}!<br />
              ✅ MandiMitra पर स्वागत है।<br />
              📍 {mandi}, {district}<br />
              ⏰ आपको सुबह 7 बजे भाव मिलेगा।<br />
              <br />
              STOP लिखें बाहर निकलने के लिए।<br />
              📲 mandibhav.in
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Done / Success screen ─────────────────────────────── //
  if (step === "done") {
    return (
      <div style={{ maxWidth: 480, margin: "60px auto", padding: "0 16px", textAlign: "center", fontFamily: bf }}>
        <div
          style={{
            background:   "#fff",
            borderRadius: 24,
            padding:      "48px 32px",
            boxShadow:    "0 8px 40px rgba(0,0,0,0.1)",
            border:       "1px solid #e8e0d0",
          }}
        >
          <div style={{ fontSize: 60, marginBottom: 14 }}>🎉</div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, color: "#1a4d2e", marginBottom: 10 }}>
            {lang === "en" ? "You're All Set!" : "बधाई हो!"}
          </h2>
          <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.7 }}>{t.thankYou}</p>

          {/* Subscription summary */}
          <div style={{ marginTop: 22, padding: 18, background: "#f0f7f4", borderRadius: 12, textAlign: "left" }}>
            <div style={{ fontWeight: 700, color: "#1a4d2e", marginBottom: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
              📋 {lang === "en" ? "Your Subscription" : "सदस्यता विवरण"}
            </div>
            <div style={{ fontSize: 13, color: "#555", lineHeight: 2.1, fontFamily: "'DM Sans', sans-serif" }}>
              <div>👤 {name}</div>
              <div>📱 +91 {mobile}</div>
              <div>🗺️ {district}</div>
              <div>🏪 {mandi}</div>
              <div>⏰ {lang === "en" ? "Daily 7:00 AM" : "प्रतिदिन सुबह 7:00"}</div>
            </div>
          </div>

          {/* Confirmation badge */}
          <div
            style={{
              marginTop:    18,
              background:   "#dcfce7",
              borderRadius: 12,
              padding:      "12px 16px",
              display:      "flex",
              alignItems:   "center",
              gap:          10,
            }}
          >
            <span style={{ fontSize: 24 }}>✅</span>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontWeight: 700, color: "#15803d", fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>
                {lang === "en" ? "WhatsApp Confirmation Sent!" : "WhatsApp पुष्टि भेज दी गई!"}
              </div>
              <div style={{ fontSize: 12, color: "#166534", fontFamily: "'DM Sans', sans-serif" }}>
                {lang === "en" ? `Delivered to +91 ${mobile}` : `+91 ${mobile} पर पहुंच गया`}
              </div>
            </div>
          </div>

          <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 14, fontFamily: "'DM Sans', sans-serif" }}>
            {lang === "en" ? 'Reply "STOP" anytime to unsubscribe.' : '"STOP" लिखकर कभी भी बाहर निकलें।'}
          </p>
        </div>
      </div>
    );
  }

  // ── Registration form ─────────────────────────────────── //
  const dropdownStyle = (disabled) => ({
    ...inputStyle(false),
    appearance:          "none",
    backgroundImage:     `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23666' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
    backgroundRepeat:    "no-repeat",
    backgroundPosition:  "right 12px center",
    cursor:              disabled ? "not-allowed" : "pointer",
    opacity:             disabled ? 0.45 : 1,
  });

  const fieldLabel = (
    <span
      style={{
        display:       "block",
        fontSize:      11,
        fontWeight:    700,
        color:         "#444",
        marginBottom:  5,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        fontFamily:    "'DM Sans', sans-serif",
      }}
    />
  );

  return (
    <div style={{ maxWidth: 520, margin: "36px auto", padding: "0 16px 60px", fontFamily: bf }}>
      {/* Page header */}
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(22px,5vw,32px)", color: "#1a4d2e", marginBottom: 6 }}>
          📲 {t.whatsapp}
        </h1>
        <p style={{ color: "#6b7280", fontSize: 14 }}>
          {lang === "en"
            ? "Register once → Get instant WhatsApp confirmation + Daily 7 AM rates"
            : "एक बार पंजीकरण → तुरंत WhatsApp पुष्टि + रोज़ 7 बजे भाव"}
        </p>
      </div>

      {/* Form card */}
      <div
        style={{
          background:   "#fff",
          borderRadius: 20,
          padding:      "28px 26px",
          boxShadow:    "0 4px 32px rgba(0,0,0,0.09)",
          border:       "1px solid #e8e0d0",
        }}
      >
        {/* Name */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#444", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: "'DM Sans', sans-serif" }}>
            {t.yourName} *
          </label>
          <input
            type="text"
            placeholder={lang === "en" ? "Full name" : "पूरा नाम"}
            value={name}
            onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: false })); }}
            style={inputStyle(errors.name)}
          />
          {errors.name && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3, fontFamily: "'DM Sans', sans-serif" }}>Required</div>}
        </div>

        {/* Mobile */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#444", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: "'DM Sans', sans-serif" }}>
            {t.mobile} *
          </label>
          <input
            type="tel"
            placeholder="10-digit mobile number"
            value={mobile}
            onChange={(e) => { setMobile(e.target.value); setErrors((p) => ({ ...p, mobile: false })); }}
            style={inputStyle(errors.mobile)}
          />
          {errors.mobile && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3, fontFamily: "'DM Sans', sans-serif" }}>Valid 10-digit number required</div>}
        </div>

        {/* State */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#444", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: "'DM Sans', sans-serif" }}>
            🗾 {t.state} *
          </label>
          <select
            style={dropdownStyle(false)}
            value={state}
            onChange={(e) => { setState(e.target.value); setDistrict(""); setMandi(""); setErrors((p) => ({ ...p, state: false })); }}
          >
            <option value="">{t.selState}</option>
            {Object.keys(STATES_DATA).map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>

        {/* Division / District */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#444", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: "'DM Sans', sans-serif" }}>
            🗺️ {t.district} *
          </label>
          <select
            style={dropdownStyle(!state)}
            disabled={!state}
            value={district}
            onChange={(e) => { setDistrict(e.target.value); setMandi(""); setErrors((p) => ({ ...p, district: false })); }}
          >
            <option value="">{t.selDist}</option>
            {districts.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>

        {/* Mandi */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#444", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: "'DM Sans', sans-serif" }}>
            🏪 {t.mandi} *
          </label>
          <select
            style={dropdownStyle(!district)}
            disabled={!district}
            value={mandi}
            onChange={(e) => { setMandi(e.target.value); setErrors((p) => ({ ...p, mandi: false })); }}
          >
            <option value="">{t.selMandi}</option>
            {mandis.map((m) => <option key={m}>{m}</option>)}
          </select>
        </div>

        {/* Instant acknowledgement info */}
        <div
          style={{
            background:   "#f0fdf4",
            border:       "1px solid #bbf7d0",
            borderRadius: 10,
            padding:      "10px 14px",
            marginBottom: 20,
            display:      "flex",
            alignItems:   "flex-start",
            gap:          10,
          }}
        >
          <span style={{ fontSize: 20 }}>⚡</span>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#15803d", lineHeight: 1.5 }}>
            <strong>{lang === "en" ? "Instant WhatsApp Confirmation" : "तुरंत WhatsApp पुष्टि"}</strong>
            <br />
            {lang === "en"
              ? "You'll receive a welcome message the moment you register."
              : "पंजीकरण के तुरंत बाद आपके WhatsApp पर स्वागत संदेश आएगा।"}
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={submit}
          style={{
            width:        "100%",
            padding:      "14px",
            background:   "#1a4d2e",
            color:        "#fff",
            border:       "none",
            borderRadius: 12,
            fontSize:     15,
            fontWeight:   600,
            cursor:       "pointer",
            fontFamily:   bf,
            boxShadow:    "0 4px 20px rgba(26,77,46,0.3)",
          }}
        >
          📲 {t.submitReg}
        </button>

        <p style={{ textAlign: "center", fontSize: 11, color: "#9ca3af", marginTop: 12, fontFamily: "'DM Sans', sans-serif" }}>
          {lang === "en" ? 'Reply "STOP" to unsubscribe anytime.' : '"STOP" लिखकर कभी भी बाहर निकलें।'}
        </p>
      </div>

      {/* Sample WhatsApp message preview */}
      <div style={{ marginTop: 24, background: "#075E54", borderRadius: 18, padding: "20px 24px" }}>
        <div style={{ fontSize: 10, opacity: 0.6, color: "#fff", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10, fontFamily: "'DM Sans', sans-serif" }}>
          {lang === "en" ? "Instant Welcome Message (Sample)" : "तुरंत स्वागत संदेश (नमूना)"}
        </div>

        {/* Welcome bubble */}
        <div
          style={{
            background:   "#DCF8C6",
            color:        "#333",
            borderRadius: "14px 14px 14px 3px",
            padding:      "12px 14px",
            fontFamily:   "monospace",
            fontSize:     13,
            lineHeight:   1.9,
            maxWidth:     300,
          }}
        >
          🌾 नमस्ते {name || "किसान"}!<br />
          ✅ MandiMitra पर स्वागत है।<br />
          📍 {mandi || "[आपकी मंडी]"}, {district || "[जिला]"}<br />
          ⏰ सुबह 7 बजे भाव मिलेगा।<br />
          <br />
          STOP लिखें बाहर निकलने के लिए।<br />
          📲 mandibhav.in
        </div>

        {/* Daily rates bubble */}
        <div
          style={{
            marginTop:    12,
            background:   "#DCF8C6",
            color:        "#333",
            borderRadius: "14px 14px 14px 3px",
            padding:      "12px 14px",
            fontFamily:   "monospace",
            fontSize:     13,
            lineHeight:   1.9,
            maxWidth:     300,
          }}
        >
          🌾 आज का मंडी भाव<br />
          📍 {mandi || "Muhana Mandi"}<br />
          📅 {new Date().toLocaleDateString("en-IN")}<br />
          <br />
          गेहूं: ₹2100-₹2300 (₹2200)<br />
          प्याज: ₹1500-₹2200 (₹1850)<br />
          आलू: ₹900-₹1400 (₹1150)<br />
          <br />
          📲 mandibhav.in
        </div>
      </div>
    </div>
  );
}
